import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { logger } from '../../lib/logger';
import { trackAffiliateLanding } from '../../lib/pixel';

/**
 * AffiliateLandingPage — Parking route at `/aff/:code`.
 *
 * Flow:
 *   1. Read the affiliate `code` from the URL.
 *   2. Validate against the backend (`/api/public/affiliate/track/{code}`).
 *   3. Persist the validated code in sessionStorage (`gp_affiliate_code`)
 *      so the Form-1 popup later forwards it to the lead-creation endpoint.
 *   4. Redirect to the landing page (`/`) and let the standard VSL gate flow run.
 *
 * Behaviour on invalid code: we still redirect to `/` (graceful degradation —
 * we don't want bad partners' typos to scare visitors away). The lead simply
 * won't be attributed to anyone.
 */
export default function AffiliateLandingPage() {
  const { code } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ok | unknown
  const [affName, setAffName] = useState('');

  useEffect(() => {
    if (!code) {
      nav('/', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/affiliate/track/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (r?.data?.ok && r?.data?.affiliate?.code) {
          // Persist for the Form-1 popup hook
          try {
            sessionStorage.setItem('gp_affiliate_code', r.data.affiliate.code);
            sessionStorage.setItem('gp_affiliate_name', r.data.affiliate.name || '');
          } catch (err) {
            logger.debug('affiliate sessionStorage write failed', err);
          }
          // ★ Meta Pixel: AffiliateLanding (custom audience for partner traffic).
          try {
            trackAffiliateLanding({
              code: r.data.affiliate.code,
              name: r.data.affiliate.name,
            });
          } catch (err) { logger.debug('trackAffiliateLanding failed', err); }
          setAffName(r.data.affiliate.name || '');
          setStatus('ok');
        } else {
          setStatus('unknown');
        }
      } catch (err) {
        logger.debug('affiliate tracking failed', err);
        setStatus('unknown');
      }
      // Redirect in ~900ms either way so we don't block the visitor.
      setTimeout(() => { if (!cancelled) nav('/', { replace: true }); }, 900);
    })();
    return () => { cancelled = true; };
  }, [code, nav]);

  return (
    <div
      className="min-h-screen bg-[var(--gp-forest-dark,#082019)] text-white grid place-items-center px-4"
      data-testid="affiliate-landing-page"
    >
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[var(--gp-gold-soft,#e6d4a8)] mb-3">
          ✦ Programma Affiliati
        </div>
        <h1 className="font-serif text-3xl md:text-4xl mb-4">Benvenuto su Green Paradise</h1>
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-3 text-white/70">
            <Loader2 className="w-6 h-6 animate-spin" data-testid="affiliate-loading" />
            <p>Verifica del link in corso…</p>
          </div>
        )}
        {status === 'ok' && (
          <div className="flex flex-col items-center gap-3" data-testid="affiliate-ok">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            <p className="text-white/90">
              {affName ? <>Sei stato invitato da <b>{affName}</b>.</> : 'Link verificato.'}
            </p>
            <p className="text-white/60 text-sm">Ti stiamo portando all'esperienza…</p>
          </div>
        )}
        {status === 'unknown' && (
          <div className="flex flex-col items-center gap-3 text-white/70" data-testid="affiliate-unknown">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
            <p>Link affiliato non riconosciuto, ma puoi comunque scoprire Green Paradise.</p>
            <p className="text-white/50 text-sm">Reindirizzamento in corso…</p>
          </div>
        )}
      </div>
    </div>
  );
}
